import React, { useEffect, useMemo, useState } from 'react';
import { isEqual, find } from 'lodash';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { useClientUIContext } from './context/ClientByIdUIContext';
import { usersService } from '../../../services';
import { dateFns } from '../../../helpers';
import { ProfileCircleSVG } from '../../../components';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import GeneralSection from './forms/sections/GeneralSection';
import { useTranslation } from 'react-i18next';
import { getColor } from '../../../helpers/getColor';
import CustomAlert from '../../../components/Alert';

export default function ClientDetailsById () {
    const { t } = useTranslation();
    // Get client Id from url params
    const { id } = useParams();
    const clientId = parseInt(id);

    // State
    const clientUIContext = useClientUIContext();
    const clientUIProps = useMemo(() => {
        return {
            isLoading: clientUIContext?.isLoading,
            setIsLoading: clientUIContext?.setIsLoading,
            clientGeneral: clientUIContext?.clientGeneral,
            setClientGeneral: clientUIContext?.setClientGeneral,
            clientAddress: clientUIContext?.clientAddress,
            setClientAddress: clientUIContext?.setClientAddress
        };
    }, [clientUIContext]);
    const [generalData, setGeneralData] = useState();
    const [error, setError] = useState(false);

    // Alert states

    const [openAlert, setOpenAlert] = useState(false);
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });

    const handleCancel = () => {
        clientUIProps.setIsLoading(false);
        clientUIProps.setClientGeneral(generalData);
    };

    const fetchClientById = () => {
        setError(false);
        clientUIProps.setIsLoading(true);

        usersService
            .getById(clientId)
            .then(async client => {
                // Parse the birth date because JS extracts one day
                if (client.person?.attributes?.born_date?.value) {
                    const parseBirthDate = dateFns.addDays(new Date(client.person.attributes.born_date.value), 1);
                    client.person.attributes.born_date.value = dateFns.format(parseBirthDate, 'yyyy-MM-dd');
                }
                else {
                    client.person.attributes.born_date = { value: '' };
                }

                setGeneralData(prepareGeneralData(client));
                clientUIProps.setClientGeneral(prepareGeneralData(client));
                clientUIProps.setIsLoading(false);

                return false;
            })
            .catch(e => {
                setError(true);
                setAlert({
                    isOpen: true,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.generalDescription'),
                    className: 'clientsMC_Alert--error'
                });
                console.error(e);
                clientUIProps.setIsLoading(false);
            });
    };

    const prepareGeneralData = (client) => {
        const gender = client.person?.attributes?.gender?.value;
        return {
            bornDate: client.person?.attributes?.born_date?.value,
            gender: gender === 'M' ? t('details.inputs.male') : gender === 'F' ? t('details.inputs.female') : null,
            nationality: 'Argentino',
            civilStatus: client.person?.attributes.marital_status ? client.person?.attributes.marital_status.value : '',
            dni: client.person?.document ? client.person?.document.description : '',
            cuil: client.person?.cuil?.description || client.person?.cuit?.description || '',
            occupation: client.person?.attributes.economic_activity?.value || '',
            telephone: client.person?.telephone ? client.person?.telephone.description : '',
            email: client.person?.email ? client.person?.email.description : '',
            devices: client.person?.devices || [],
            FATCA: !!Number(client.person?.attributes?.facta_ocddt?.value),
            PEP: !!Number(client.person?.attributes?.politically_exposed_person?.value),
            reporting_parties: !!Number(client.person?.attributes?.reporting_parties?.value),
            level: client.person?.levels?.length > 0 ? client.person.levels[0].id_level : '',
            dwellTime: client.person?.attributes?.dwell_time?.value || ''
        };
    };

    const handleSubmit = () => {
        clientUIProps.setIsLoading(true);
        if (!validateClient() && !validateDevices()) {
            clientUIProps.setIsLoading(false);
            setAlert({
                isOpen: true,
                severity: 'warning',
                description: 'No se realizó ningun cambio en los campos.',
                className: 'clientsMC_Alert--warning'
            });
            return false;
        }

        // Validate Email
        if (!validateEmail()) {
            clientUIProps.setIsLoading(false);
            setAlert({
                isOpen: true,
                severity: 'warning',
                description: 'El email es inválido.',
                className: 'clientsMC_Alert--warning'
            });
            return false;
        }
        // Validate for changes in client
        if (validateClient()) {
            const data = prepareData();

            usersService
                .editById(clientId, data)
                .then(client => {
                    clientUIProps.setIsLoading(false);
                    setAlert({
                        isOpen: true,
                        severity: 'success',
                        description: 'Los cambios fueron guardados correctamente.',
                        className: 'clientsMC_Alert--success'
                    });
                })
                .catch(e => {
                    clientUIProps.setIsLoading(false);
                    console.error(e);
                    if (e.response.data.extra.from === 'telephones') {
                        setAlert({
                            isOpen: true,
                            severity: 'warning',
                            description: 'El número de celular es inválido.',
                            className: 'clientsMC_Alert--warning'
                        });
                    }
                    else {
                        setAlert({
                            isOpen: true,
                            severity: 'error',
                            title: 'Algo no salió como esperamos',
                            description: 'Lo sentimos, en este momento no fue posible completar la operación.',
                            className: 'clientsMC_Alert--error'
                        });
                    }
                });
        }

        // Validate for changes in devices
        for (const device of clientUIProps.clientGeneral.devices) {
            if (validateDevice(device)) {
                const data = {
                    idDevice: device.id,
                    blocked: device.blocked
                };

                usersService
                    .blackList(clientId, data)
                    .then(user => {
                        generalData.devices.map(origDevice => {
                            if (origDevice.id === device.id) {
                                origDevice.active = !origDevice.active;
                            }

                            return origDevice;
                        });
                        clientUIProps.setIsLoading(false);
                        setAlert({
                            isOpen: true,
                            severity: 'success',
                            description: 'Los cambios fueron guardados correctamente.',
                            className: 'clientsMC_Alert--success'
                        });
                        return false;
                    })
                    .catch(e => {
                        clientUIProps.setIsLoading(false);
                        console.error(e);
                        setAlert({
                            isOpen: true,
                            severity: 'error',
                            title: t('errors.generalTitle'),
                            description: t('errors.generalDescription'),
                            className: 'clientsMC_Alert--error'
                        });
                        return false;
                    });
            }
        }
    };

    // Functions

    useEffect(() => {
        fetchClientById(clientId);
        // eslint-disable-next-line
    }, []);

    const validateClient = () => {
        // Take the devices to check only the other props
        // eslint-disable-next-line no-unused-vars
        const { devices: _, ...noDevices } = generalData;
        // eslint-disable-next-line no-unused-vars
        const { devices, ...noClientDevices } = clientUIProps.clientGeneral;

        // if (isEqual(noDevices, noClientDevices) && isEqual(addressData, clientUIProps.clientAddress)) {
        //     return false;
        // }
        return true;
    };

    const validateDevices = () => {
        if (isEqual(generalData.devices, clientUIProps.clientGeneral.devices)) {
            return false;
        }
        return true;
    };

    const validateDevice = device => {
        const prevDevice = find(generalData.devices, ['id', device.id]);

        if (isEqual(prevDevice, device)) {
            return false;
        }

        return true;
    };

    const validateEmail = () => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (regex.test(clientUIProps.clientGeneral.email)) {
            return true;
        }
    };

    const prepareData = () => {
        const data = {
            person: {
                attributes: [],
                emails: [],
                telephones: [],
                addresses: []
            }
        };
        // Prepare General Data

        if (clientUIProps.clientGeneral.gender !== generalData.gender) {
            data.person.attributes.push({ description: 'gender', value: clientUIProps.clientGeneral.gender[0] });
        }

        if (clientUIProps.clientGeneral.civilStatus !== generalData.civilStatus) {
            data.person.attributes.push({ description: 'marital_status', value: clientUIProps.clientGeneral.civilStatus });
        }

        if (clientUIProps.clientGeneral.bornDate !== generalData.bornDate) {
            const parseDate = dateFns.format(clientUIProps.clientGeneral.bornDate, 'y-MM-dd');
            data.person.attributes.push({ description: 'born_date', value: parseDate });
        }

        if (clientUIProps.clientGeneral.occupation !== generalData.occupation) {
            data.person.attributes.push({ description: 'economic_activity', value: clientUIProps.clientGeneral.occupation });
        }

        if (clientUIProps.clientGeneral.email !== generalData.email) {
            data.person.emails.push({ idType: 1, email: clientUIProps.clientGeneral.email });
        }

        if (clientUIProps.clientGeneral.telephone !== generalData.telephone) {
            data.person.telephones.push({ idType: 1, telephone: '+549' + clientUIProps.clientGeneral.telephone, country: 'AR' });
        }

        // Prepare Address data
        data.person.address = clientUIProps.clientAddress;

        // Delete not used props
        if (!data.person.attributes.length) {
            delete data.person.attributes;
        }
        if (!data.person.emails.length) {
            delete data.person.emails;
        }
        if (!data.person.telephones.length) {
            delete data.person.telephones;
        }
        if (!data.person.addresses.length) {
            delete data.person.addresses;
        }

        return data;
    };

    // Main Component

    return (
        <>
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />
            <Box style={{ backgroundColor: '#FFF', boxShadow: '0px 0px 30px 0px rgba(82, 63, 105, 0.06)' }} width={'100%'} marginTop={'1rem'} padding={'1rem'} >
                <Box
                    gap={'1rem'}>
                    <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'>
                        <Box display='flex' gap={'8px'}>
                            {<ProfileCircleSVG customStroke={getColor('primary', 700)}/>}
                            <Typography variant='h5'>{t('details.title')}</Typography>
                        </Box>
                    </Box>
                    {
                        error && (<Typography sx={{ color: 'red', textAlign: 'center' }}>{t('errors.notFound')}</Typography>
                        )
                    }
                    {
                        clientUIProps.isLoading && (<Stack alignItems={'center'}><CircularProgress size={24} style={{ color: 'primary' }} /></Stack>)

                    }
                    {
                        generalData && (<GeneralSection client={generalData} handleCancel={handleCancel} handleSubmit={handleSubmit} />)

                    }
                </Box>

            </Box>
        </>
    );
}
